import config from '@wandb/common/config';
import React from 'react';
import {Route} from 'react-router';

import {createProjectPage} from '../util/urls';
import {
  ACCOUNT_SETTINGS_PATHS,
  ARTIFACT_VERSION_PATH,
  BENCHMARK_PATHS,
  ENTITY_LAUNCH_PATHS,
  ENTITY_REGISTRY_ACCESS_CONTROL_PATHS,
  ENTITY_REGISTRY_PATHS,
  GALLERY_CREATE_POST,
  GALLERY_INTERNAL_ADMIN_PATH,
  GALLERY_PATH,
  GALLERY_PATH_SEGMENT,
  GALLERY_POST_EDIT_PATH,
  GALLERY_POST_VIEW_PATH,
  GROUP_PATHS,
  JOB_PATHS,
  NO_ACCESS_PATH,
  OLD_GALLERY_DISCUSSION_EDIT_PATH,
  OLD_GALLERY_DISCUSSION_VIEW_PATH,
  OLD_REPORT_PAGE_EDIT_PATH,
  OLD_REPORT_PAGE_VIEW_PATH,
  ONBOARDING_FLOW_PATH,
  PROFILE_LIKES_PATH,
  PROFILE_PATH,
  PROFILE_PROJECTS_PATH,
  PROFILE_REPORTS_PATH,
  PROJECT_BASE_PATH,
  PROJECT_PATHS,
  QUEUE_PATHS,
  QUICKSTART_PATH,
  REPORT_PAGE_EDIT_PATH,
  REPORT_PAGE_VIEW_PATH,
  REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
  RUN_PATHS,
  STORAGE_EXPLORER_TRACKED_PATH,
  SWEEP_PATHS,
  TEAM_MEMBERS_PATH,
  TEAM_SETTINGS_PATH,
} from './paths';
import PrivateRoute from './PrivateRoute';
import RouteWithLayout from './RouteWithLayout';

const IS_ON_PREM = config.ENVIRONMENT_IS_PRIVATE;

export const ROUTES_DATA = [
  {
    RouteComponent: Route,
    path: '/__test_page',
    component: React.lazy(() => import('../pages/TestPage')),
    exact: true,
  },
  {
    RouteComponent: Route,
    path: ['/__registry', '/__registry/:tab'],
    component: React.lazy(() => import('../components/Panel2/RegistryPage')),
    exact: true,
    routeName: 'Registry',
  },
  {
    RouteComponent: Route,
    path: ['/__wbench', '/__wbench/:tab'],
    component: React.lazy(
      () => import('../components/Panel2/InternalBenchmarkPage')
    ),
    exact: true,
  },
  {
    RouteComponent: RouteWithLayout,
    path: '/',
    component: React.lazy(() => import('../pages/HomePage/HomePageRouter')),
    exact: true,
    routeName: 'Home',
  },
  {
    RouteComponent: RouteWithLayout,
    path: '/login',
    component: React.lazy(() => import('../components/Login')),
    routeName: 'Login',
  },
  {
    RouteComponent: RouteWithLayout,
    path: '/login-redirect',
    component: React.lazy(() => import('../components/LoginRedirect')),
  },
  {
    RouteComponent: Route,
    path: '/logout',
    component: React.lazy(() => import('../components/Logout')),
    routeName: 'Logout',
  },
  {
    RouteComponent: RouteWithLayout,
    path: '/trending',
    component: React.lazy(() => import('../pages/TrendingRepos')),
    routeName: 'Trending',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/home',
    component: React.lazy(() => import('../pages/HomePage/HomePageWithData')),
    routeCache: 'HomePage',
    exact: true,
    routeName: 'Home',
  },
  {
    RouteComponent: RouteWithLayout,
    path: QUICKSTART_PATH,
    component: React.lazy(() => import('../pages/HomePage/HomePageWithData')),
    routeCache: 'QuickStartPage',
    routeName: 'QuickStart',
  },
  {
    RouteComponent: PrivateRoute,
    path: GALLERY_CREATE_POST, // Go to fully-connected -> click on WRITE POST
    component: React.lazy(() => import('../pages/GalleryCreatePost')),
    routeCache: 'GalleryCreatePost',
    routeName: 'FullyConnectedCreatePost',
  },
  {
    RouteComponent: RouteWithLayout,
    path: GALLERY_PATH,
    component: React.lazy(() => import('../pages/FC')),
    routeName: 'FullyConnected',
  },
  {
    path: '/fullyconnected',
    redirect: `/${GALLERY_PATH_SEGMENT}`,
  },
  {
    path: '/fc',
    redirect: `/${GALLERY_PATH_SEGMENT}`,
  },
  {
    path: '/gallery',
    redirect: `/${GALLERY_PATH_SEGMENT}`,
  },
  {
    RouteComponent: RouteWithLayout,
    path: '/__test/simple_error',
    component: React.lazy(() => import('../pages/ErrorTestPage')),
    exact: true,
  },
  {
    RouteComponent: PrivateRoute,
    path: ONBOARDING_FLOW_PATH,
    component: React.lazy(() => import('../pages/OnboardingFlow')),
    routeCache: 'OnboardingFlow',
    routeName: 'OnboardingFlow',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/change-password',
    component: React.lazy(() => import('../pages/ChangePassword')),
    routeCache: 'ChangePassword',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/authorize',
    component: React.lazy(() => import('../pages/AuthorizePage')),
    routeCache: 'AuthorizePage',
    allowIframes: true,
  },
  {
    RouteComponent: PrivateRoute,
    path: '/settings',
    component: React.lazy(
      () => import('../components/UserSettings/UserSettingsPage')
    ),
    routeCache: 'UserSettingsPage',
    exact: true,
    routeName: 'UserSettings',
  },
  {
    RouteComponent: PrivateRoute,
    path: ACCOUNT_SETTINGS_PATHS,
    component: React.lazy(() => import('../pages/HomePage/HomePageWithData')),
    routeCache: 'AccountSettingsPage',
    exact: true,
    condition: !IS_ON_PREM,
    routeName: 'AccountSettings',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/admin/usage',
    component: React.lazy(() => import('../pages/UsageAdmin')),
    routeCache: 'UsageAdmin',
    exact: true,
    condition: IS_ON_PREM,
    routeName: 'AdminUsage',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/admin/organization',
    component: React.lazy(
      () => import('../wandb-admin/pages/OrganizationAdminPage')
    ),
    routeCache: 'OrganizationAdmin',
    routeName: 'AdminOrganization',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/admin/users',
    component: React.lazy(
      () => import('../pages/UsersAdminPage/UsersAdminPage')
    ),
    routeCache: 'UsersAdmin',
    routeName: 'AdminUser',
  },
  {
    RouteComponent: PrivateRoute,
    path: GALLERY_INTERNAL_ADMIN_PATH,
    component: React.lazy(() => import('../pages/GalleryAdmin')),
    routeCache: 'GalleryAdmin',
    routeName: 'AdminFullyConnected',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/admin/ramp',
    component: React.lazy(() => import('../pages/RampAdmin')),
    routeCache: 'RampAdmin',
    routeName: 'AdminRamp',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/admin/experiments',
    component: React.lazy(
      () => import('../wandb-admin/pages/ExperimentAdminPage')
    ),
    routeCache: 'ExperimentAdminPage',
    routeName: 'AdminExperiment',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/create-team',
    component: React.lazy(() => import('../pages/CreateTeam')),
    routeCache: 'CreateTeam',
    routeName: 'CreateTeam',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/admin/home',
    component: React.lazy(() => import('../wandb-admin/pages/HomeAdminPage')),
    routeCache: 'AdminDashboard',
    routeName: 'AdminHome',
  },
  {
    RouteComponent: RouteWithLayout,
    path: [
      '/usage/:entityName',
      STORAGE_EXPLORER_TRACKED_PATH,
      '/usage/:entityName/:filePath*',
    ],
    component: React.lazy(() => import('../pages/StorageExplorer')),
    routeCache: 'StorageExplorer',
    exact: true,
    allowAnonymous: true,
    routeName: 'StorageExplorer',
  },
  {
    path: '/storage/:entityName/tracked/:filePath*',
    redirect: STORAGE_EXPLORER_TRACKED_PATH,
  },
  {
    path: '/storage/:entityName/:filePath*',
    redirect: '/usage/:entityName/:filePath*',
  },
  {
    path: '/storage/:entityName',
    redirect: '/usage/:entityName',
  },
  {
    RouteComponent: PrivateRoute,
    path: createProjectPage(),
    component: React.lazy(() => import('../pages/CreateProject')),
    routeCache: 'NewProject',
    routeName: 'CreateProject',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/new-benchmark',
    component: React.lazy(() => import('../pages/NewBenchmark')),
    routeCache: 'NewBenchmark',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/:entityname/benchmark/new',
    component: React.lazy(() => import('../pages/NewBenchmark')),
    routeCache: 'NewBenchmarkPage',
  },
  {
    RouteComponent: RouteWithLayout,
    path: PROFILE_REPORTS_PATH,
    component: React.lazy(() => import('../pages/EntityPage')),
    routeCache: 'EntityPage',
    exact: true,
    additionalProps: {tab: 'reports'},
    // note that here and below, we rely on the fact that we include the tab
    // when we log the route name
    routeName: 'Entity',
  },
  {
    RouteComponent: RouteWithLayout,
    path: PROFILE_PROJECTS_PATH,
    component: React.lazy(() => import('../pages/EntityPage')),
    routeCache: 'EntityPage',
    exact: true,
    additionalProps: {tab: 'projects'},
    routeName: 'Entity',
  },
  {
    RouteComponent: RouteWithLayout,
    path: PROFILE_LIKES_PATH,
    component: React.lazy(() => import('../pages/EntityPage')),
    routeCache: 'EntityPage',
    exact: true,
    additionalProps: {tab: 'likes'},
    routeName: 'Entity',
  },
  {
    RouteComponent: RouteWithLayout,
    path: TEAM_MEMBERS_PATH,
    component: React.lazy(() => import('../pages/EntityPage')),
    routeCache: 'EntityPage',
    exact: true,
    additionalProps: {tab: 'members'},
    routeName: 'Entity',
  },
  {
    RouteComponent: RouteWithLayout,
    path: TEAM_SETTINGS_PATH,
    component: React.lazy(() => import('../pages/EntityPage')),
    routeCache: 'EntityPage',
    exact: true,
    additionalProps: {tab: 'settings'},
    routeName: 'Entity',
  },
  // TODO: Remove! Keeping for backwards compatibility (email links, etc)
  {
    RouteComponent: RouteWithLayout,
    path: '/teams/:entityName',
    component: React.lazy(() => import('../pages/EntityPage')),
    routeCache: 'EntityPage',
    exact: true,
    additionalProps: {tab: 'settings'},
    routeName: 'Entity',
  },
  {
    RouteComponent: RouteWithLayout,
    path: ENTITY_REGISTRY_PATHS,
    component: React.lazy(() => import('../pages/EntityRegistryPage')),
    routeCache: 'EntityPage',
    exact: true,
    routeName: 'EntityRegistry',
  },
  {
    RouteComponent: RouteWithLayout,
    path: ENTITY_REGISTRY_ACCESS_CONTROL_PATHS,
    component: React.lazy(() => import('../pages/HomePage/HomePageWithData')),
    routeCache: 'EntityRegistryAccessControl',
    exact: true,
    routeName: 'EntityRegistryAccessControl',
  },
  {
    path: `${ARTIFACT_VERSION_PATH}/graph`,
    redirect: `${ARTIFACT_VERSION_PATH}/lineage`,
  },
  {
    path: '/models',
    redirect: '/registry/model',
  },
  {
    RouteComponent: RouteWithLayout,
    path: ENTITY_LAUNCH_PATHS,
    component: React.lazy(() => import('../pages/HomePage/HomePageWithData')),
    routeCache: 'EntityPage',
    exact: true,
    routeName: 'EntityLaunch',
  },
  {
    RouteComponent: PrivateRoute,
    path: RUN_PATHS,
    component: React.lazy(() => import('../pages/RunPage2')),
    routeCache: 'RunPage2',
    exact: true,
    allowIframes: true,
    allowAnonymous: true,
    routeName: 'Run',
  },
  {
    RouteComponent: PrivateRoute,
    path: GROUP_PATHS,
    component: React.lazy(() => import('../pages/GroupPage')),
    routeCache: 'GroupPage',
    exact: true,
    allowIframes: true,
    allowAnonymous: true,
    routeName: 'Group',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/:entityName/:projectName/create-sweep',
    component: React.lazy(() => import('../pages/CreateSweepPage')),
    routeCache: 'CreateSweepPage',
    allowIframes: true,
    exact: true,
    routeName: 'CreateSweep',
  },
  {
    RouteComponent: PrivateRoute,
    path: SWEEP_PATHS,
    component: React.lazy(() => import('../pages/SweepPage')),
    routeCache: 'SweepPage',
    exact: true,
    allowIframes: true,
    allowAnonymous: true,
    routeName: 'Sweep',
  },
  {
    RouteComponent: PrivateRoute,
    path: JOB_PATHS,
    component: React.lazy(() => import('../pages/JobPage')),
    routeCache: 'JobPage',
    exact: true,
    allowIframes: true,
    allowAnonymous: true,
    routeName: 'Job',
  },
  {
    RouteComponent: PrivateRoute,
    path: QUEUE_PATHS,
    component: React.lazy(() => import('../pages/HomePage/HomePageWithData')),
    routeCache: 'QueuePage',
    exact: true,
    allowIframes: true,
    allowAnonymous: true,
    routeName: 'Queue',
  },
  {
    RouteComponent: PrivateRoute,
    path: BENCHMARK_PATHS,
    component: React.lazy(() => import('../pages/Benchmark/BenchmarkPage')),
    routeCache: 'BenchmarkPage',
    exact: true,
    allowIframes: true,
    allowAnonymous: true,
    routeName: 'Benchmark',
  },
  // TODO - remove these related logics
  // {
  //   RouteComponent: PrivateRoute,
  //   path: `/:entityName/${PUBLISHED_WORK_PATH_SEGMENT}`,
  //   component: React.lazy(() => import('../pages/PublishedWorkPage')),
  //   routeCache: 'PublishedWorkPage',
  //   exact: true,
  //   allowAnonymous: true,
  // },
  {
    // This old edit report page route will be redirected to /reportlist
    RouteComponent: PrivateRoute,
    path: OLD_REPORT_PAGE_EDIT_PATH,
    component: React.lazy(() => import('../pages/ReportPage/ReportPageRouter')),
    exact: true,
  },
  {
    // This old view report page route will be redirected to /reportlist
    RouteComponent: PrivateRoute,
    path: OLD_REPORT_PAGE_VIEW_PATH,
    component: React.lazy(() => import('../pages/ReportPage/ReportPageRouter')),
    exact: true,
  },
  {
    RouteComponent: PrivateRoute,
    path: REPORT_PAGE_EDIT_PATH,
    component: React.lazy(() => import('../pages/ReportPage/ReportPageRouter')),
    routeCache: 'ReportPageEdit',
    exact: true,
    allowIframes: true,
    allowAnonymous: true,
    routeName: 'ReportEdit',
  },
  {
    RouteComponent: PrivateRoute,
    path: REPORT_PAGE_VIEW_PATH,
    component: React.lazy(() => import('../pages/ReportPage/ReportPageRouter')),
    routeCache: 'ReportPageView',
    exact: true,
    allowIframes: true,
    allowAnonymous: true,
    routeName: 'ReportView',
  },
  {
    RouteComponent: PrivateRoute,
    path: REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
    component: React.lazy(() => import('../pages/ReportPage/ReportPageRouter')),
    routeCache: 'ReportPageViewPublishedWork',
    exact: true,
    allowIframes: true,
    allowAnonymous: true,
    routeName: 'ReportViewPublished',
  },
  {
    // This is used when you click on "Create new post". This is triggered through "Write post" in FC
    RouteComponent: PrivateRoute,
    path: GALLERY_POST_EDIT_PATH,
    component: React.lazy(() => import('../pages/ReportPage/ReportPageRouter')),
    routeCache: 'ReportPageEdit',
    exact: true,
    allowAnonymous: true,
    routeName: 'FullyConnectedPostEdit',
  },
  {
    // This is used when you view a post you submit.
    RouteComponent: PrivateRoute,
    path: GALLERY_POST_VIEW_PATH,
    component: React.lazy(() => import('../pages/ReportPage/ReportPageRouter')),
    routeCache: 'ReportPageView',
    exact: true,
    allowAnonymous: true,
    routeName: 'FullyConnectedPostView',
  },
  {
    RouteComponent: PrivateRoute,
    path: OLD_GALLERY_DISCUSSION_EDIT_PATH,
    component: React.lazy(() => import('../pages/ReportPage/ReportPageRouter')),
    routeCache: 'ReportPageEdit',
    exact: true,
    allowAnonymous: true,
    routeName: 'FullyConnectedDiscussionEdit',
  },
  {
    RouteComponent: PrivateRoute,
    path: OLD_GALLERY_DISCUSSION_VIEW_PATH,
    component: React.lazy(() => import('../pages/ReportPage/ReportPageRouter')),
    routeCache: 'ReportPageView',
    exact: true,
    allowAnonymous: true,
    routeName: 'FullyConnectedDiscussionView',
  },
  {
    RouteComponent: RouteWithLayout,
    path: '/:entityName/:projectName/querydemo',
    component: React.lazy(() => import('../pages/QueryDemoPage')),
    routeCache: 'QueryDemoPage',
    exact: true,
    allowAnonymous: true,
    routeName: 'QueryDemo',
  },
  {
    RouteComponent: RouteWithLayout,
    path: '/:entityName/:projectName/weave-sandbox',
    component: React.lazy(() => import('../pages/WeaveSandboxPage')),
    routeCache: 'WeaveSandboxPage',
    exact: true,
    allowAnonymous: true,
    routeName: 'WeaveSandbox',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/org/:orgName',
    component: React.lazy(() => import('../pages/OrganizationPage')),
    routeCache: 'OrganizationPage',
    exact: true,
    allowAnonymous: false,
    condition: !IS_ON_PREM,
    routeName: 'Organization',
  },
  {
    RouteComponent: PrivateRoute,
    path: '/repo/:repoName',
    component: React.lazy(() => import('../pages/RepoInsightsPage')),
    routeCache: 'RepoInsightsPage',
    exact: true,
    allowAnonymous: false,
    routeName: 'Repo',
  },
  {
    RouteComponent: RouteWithLayout,
    path: NO_ACCESS_PATH,
    component: React.lazy(() => import('../pages/NoAccessPage')),
    routeCache: 'NoAccessPage',
    exact: true,
    allowAnonymous: true,
    routeName: 'NoAccess',
  },
  {
    RouteComponent: RouteWithLayout,
    path: PROJECT_PATHS,
    component: React.lazy(() => import('../pages/ProjectPage')),
    routeCache: 'ProjectPage',
    exact: true,
    allowIframes: true,
    allowAnonymous: true,
    routeName: 'Workspace',
  },
  {
    // this is here to truncate extra path segments after the actual path
    path: PROJECT_BASE_PATH,
    redirect: PROJECT_BASE_PATH,
  },
  {
    RouteComponent: RouteWithLayout,
    path: PROFILE_PATH,
    component: React.lazy(() => import('../pages/EntityPage')),
    routeCache: 'EntityPage',
    exact: true,
    allowAnonymous: true,
    routeName: 'Entity',
  },
];
